import {
      doc,
      setDoc,
      updateDoc,
      onSnapshot,
      collection,
      query,
      where,
      orderBy,
      getDoc,
      getDocs,
} from "firebase/firestore";

import { db } from "../firebase";
import { getChatId } from "../utils/Helper.js";


// 🚀 SAFE function to always return valid string IDs
const safeId = (id) => {
  if (!id) return "";                // avoid undefined
  if (typeof id === "string") return id;
  if (typeof id === "number") return String(id);
  if (typeof id === "object") {
    return id.id || id.user_id || JSON.stringify(id);
  }
  return String(id);
};

const normalizeTimestamp = (value) => {
  if (!value) return 0;

  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }
  if (typeof value.toDate === "function") {
    return value.toDate().getTime();
  }

  return 0;
};

const fetchChatUser = async (userId) => {
  const normalizedId = safeId(userId);
  if (!normalizedId) {
    return {
      id: "",
      name: "",
      image: "",
      primary_id: "",
      about: "",
      is_online: false,
    };
  }

  const userSnap = await getDoc(doc(db, "users", normalizedId));
  const userData = userSnap.exists() ? userSnap.data() : {};

  return {
    id: normalizedId,
    name: userData.name || "",
    image: userData.image || "",
    primary_id: userData.primary_id || "",
    about: userData.about || "",
    is_online: Boolean(userData.is_online),
  };
};

/* ---------------- ENSURE my_users ---------------- */
const ensureMyUser = async (currentUserId, partnerId) => {
  await setDoc(
    doc(db, "users", currentUserId, "my_users", partnerId),
    {
      id: partnerId,
      lasttimestamp: String(Date.now()),
      created_at: String(Date.now()),
    },
    { merge: true }
  );
};

// 🚀 SEND MESSAGE
export const sendMessage = async (fromId, toId, msg, type = "text") => {
  try {
    const u1 = safeId(fromId);
    const u2 = safeId(toId);
    if (!u1 || !u2) return;

    const chatId = await getChatId(u1, u2);
    const chatRef = doc(db, "chats", chatId);

    // Ensure chat exists
    const chatSnap = await getDoc(chatRef);
    if (!chatSnap.exists()) {
      await setDoc(chatRef, {
        users: [u1, u2],
        createdAt: String(Date.now()),
        lastMessage: "",
        lastMessageTime: "",
        lastFrom: "",
        lastTo: "",
      });
    }

    const timestamp = Date.now().toString();

    // Save message
    await setDoc(doc(db, "chats", chatId, "messages", timestamp), {
      fromId: u1,
      toId: u2,
      msg,
      type,
      read: "",
      sent: timestamp,
    });

    // Update chat preview
    await updateDoc(chatRef, {
      lastMessage: msg,
      lastMessageTime: timestamp,
      lastFrom: u1,
      lastTo: u2,
    });

    // 🔥 ensure my_users + timestamp update (BOTH SIDES)
    await ensureMyUser(u1, u2);
    await ensureMyUser(u2, u1);

  } catch (err) {
    console.error("🔥 sendMessage ERROR:", err);
  }
};



// 🚀 LISTEN LIVE MESSAGES
export const listenMessages = async (fromId, toId, callback) => { // Must be async
    const u1 = safeId(fromId);
    const u2 = safeId(toId);

    // 💡 FIX: AWAIT the Promise
    const chatId = await getChatId(u1, u2); 

    if (!chatId) {
        console.error("Invalid chatId:", chatId, u1, u2);
        return () => {}; // return empty unsubscribe function
    }
    // ... rest of function remains the same, but now it returns a Promise
    const msgRef = collection(db, "chats", chatId, "messages");
    const q = query(msgRef, orderBy("sent", "asc"));

    // You now return a Promise that resolves to the unsubscribe function
    return onSnapshot(q, callback); 
};


// 🚀 MARK MESSAGES AS READ
export const markAsRead = async (fromId, toId) => {
  const u1 = safeId(fromId); // current user who is reading
  const u2 = safeId(toId);   // the chat partner

  const chatId = await getChatId(u1, u2);
  if (!chatId) return;

  const messagesRef = collection(db, "chats", chatId, "messages");

  
  const q = query(
    messagesRef,
    where("toId", "==", u1),
    where("read", "==", "") // match your current empty string
  );

  const snap = await getDocs(q);
  if (snap.empty) return; // nothing to update

  // Update each message with read timestamp
  const updates = snap.docs.map((docSnap) =>
    updateDoc(doc(messagesRef, docSnap.id), {
      read: Date.now(), // store as number timestamp
    })
  );

  await Promise.all(updates);
};


// 🚀 TYPING STATUS
export const setTypingStatus = async (fromId, toId, isTyping) => { // Must be async
    const u1 = safeId(fromId);
    const u2 = safeId(toId);

    const chatId = await getChatId(u1, u2); // 💡 FIX: AWAIT the Promise

    await setDoc(
        doc(db, "chats", chatId, "typing_status", u1), // chatId is now a string
        {
            typingStatus: isTyping,
            updatedAt: String(Date.now()),
        },
        { merge: true }
    );
};

// member list
export const listenChatMembers = (currentUserId, callback) => {
  const uid = safeId(currentUserId);
  if (!uid) return () => {};

  const q = query(
    collection(db, "users", uid, "my_users"),
    orderBy("lasttimestamp", "desc")
  );

  return onSnapshot(q, async (snapshot) => {
    const members = await Promise.all(
      snapshot.docs.map(async (docSnap) => {
        const partnerId = docSnap.id; // VS0015
        const meta = docSnap.data();

        // 🔥 FETCH PARTNER PROFILE
        const userSnap = await getDoc(doc(db, "users", partnerId));
        const userData = userSnap.exists() ? userSnap.data() : {};

        return {
          chatId: meta.chatId || `${uid}_${partnerId}`,
          otherUserId: partnerId,

          // 👤 PROFILE DATA
          name: userData.name || "",
          image: userData.image || "",
          about: userData.about || "",
          primary_id: userData.primary_id || "",
          is_online: userData.is_online || false,

          // 💬 CHAT META
          lastMessage: meta.lastMessage || "",
          lastMessageTime: meta.lasttimestamp
            ? meta.lasttimestamp.toMillis
              ? meta.lasttimestamp.toMillis()
              : Number(meta.lasttimestamp)
            : "",
        };
      })
    );
    callback(members);
  });
};


// ✅ Create main user
export const createUser = async (userId, data) => {
  if (!userId) return;

  await setDoc(
    doc(db, "users", userId),
    {
      id: userId,
      name: data.name || "",
      image: data.image || "",
      primary_id: data.primary_id || "",
      is_online: data.is_online ? true : false,
      about: "Hey, I'm using chat",
      created_at: Date.now().toString(),
      last_active: Date.now().toString(),
      last_active_status : data.last_active_status,
      online_status : data.online_status,
      push_token : data.push_token
    },
    { merge: true }
  );
};

// ✅ Create chat user relation
export const createChatUser = async (myId, partner) => {
  if (!myId || !partner?.id) return;

  await setDoc(
    doc(db, "users", myId, "my_users", partner.id),
    {
      id: partner.id,
      name: partner.name,
      image: partner.image,
      lastMessage: "",
      lasttimestamp: Date.now().toString(), 
      last_active_status : partner.last_active_status,
      online_status : partner.online_status,
      push_token : partner.push_token
    },
    { merge: true }
  );
};

// chatService.js
export const listenUnreadCount = async (chatId, currentUserId, callback) => {
  if (!chatId || !currentUserId) {
    callback(0);
    return () => {};
  }

  const uid = String(currentUserId);
  
  const parts = chatId.split('_');
  if (parts.length !== 2) return () => {}; 
  
  const idA = `${parts[0]}_${parts[1]}`;
  const idB = `${parts[1]}_${parts[0]}`;

  let activeId = idA; 
  const snapA = await getDoc(doc(db, "chats", idA));
  
  if (!snapA.exists()) {
    const snapB = await getDoc(doc(db, "chats", idB));
    if (snapB.exists()) {
      activeId = idB;
    }
  }

  try {
    const msgRef = collection(db, "chats", activeId, "messages");
    const q = query(
      msgRef,
      where("toId", "==", uid),
      where("read", "==", "")
    );

    return onSnapshot(q, (snapshot) => {
      callback(snapshot ? snapshot.size : 0);
    }, (err) => {
      console.error("Firestore Count Error:", err);
      callback(0);
    });
  } catch (e) {
    console.error("Setup Error:", e);
    callback(0);
    return () => {};
  }
};

export const listenAdminChats = (callback) => {
  return onSnapshot(
    collection(db, "chats"),
    async (snapshot) => {
      const chats = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const fallbackUsers = docSnap.id.split("_").filter(Boolean);
          const rawUsers = Array.isArray(data.users) && data.users.length ? data.users : fallbackUsers;
          const userIds = Array.from(new Set(rawUsers.map(safeId).filter(Boolean)));
          const users = await Promise.all(userIds.map((userId) => fetchChatUser(userId)));

          return {
            chatId: docSnap.id,
            users,
            lastMessage: data.lastMessage || "",
            lastMessageTime: normalizeTimestamp(data.lastMessageTime || data.createdAt),
            createdAt: normalizeTimestamp(data.createdAt),
            lastFrom: safeId(data.lastFrom),
            lastTo: safeId(data.lastTo),
          };
        })
      );

      chats.sort((left, right) => right.lastMessageTime - left.lastMessageTime);
      callback(chats);
    },
    (error) => {
      console.error("Admin chat list error:", error);
      callback([]);
    }
  );
};

export const listenMessagesByChatId = (chatId, callback) => {
  const normalizedChatId = safeId(chatId);
  if (!normalizedChatId) {
    callback([]);
    return () => {};
  }

  const msgRef = collection(db, "chats", normalizedChatId, "messages");
  const q = query(msgRef, orderBy("sent", "asc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const messages = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();

        return {
          id: docSnap.id,
          ...data,
          fromId: safeId(data.fromId),
          toId: safeId(data.toId),
          sent: normalizeTimestamp(data.sent || data.createdAt),
          read: normalizeTimestamp(data.read),
        };
      });

      callback(messages);
    },
    (error) => {
      console.error("Admin chat message error:", error);
      callback([]);
    }
  );
};
